/**
 * Operator Reassignment Page
 * 
 * Admin → Operations → Bulk Store & Format Reassignment
 * 
 * Two entry paths after selecting retailer + cycle:
 *   "By Operator" — pick operator, see their stores/formats
 *   "By Store"    — pick store, see all operators/formats assigned there
 * 
 * Both converge at: Select assignments → Choose new operator → Preview → Execute
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Users, 
  Store, 
  FileText, 
  ArrowRight, 
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  HelpCircle,
  ArrowRightLeft,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { apiRequest } from '@/lib/queryClient';
import { apiFetch } from '@/lib/api';
import { Combobox } from '@/components/ui/combobox';
import { 
  ReassignmentImpactModal, 
  type AssignmentChange 
} from '@/components/reassignment';

type LookupMode = 'by-operator' | 'by-store';

// Types
interface Operator {
  id: number;
  name: string;
  email?: string;
}

interface Retailer {
  id: number;
  name: string;
}

interface BookingCycle {
  id: number;
  name: string;
  retailerId: number;
  startDate?: string;
  endDate?: string;
}

interface StoreFormatAssignment {
  id: number;
  storeId: number;
  mediaFormatId: number;
  operatorId: number;
  storeName?: string;
  formatName?: string;
  retailerId?: number;
  createdAt?: string;
}

interface StoreGroup {
  storeId: number;
  storeName: string;
  formats: Array<{
    assignmentId: number;
    mediaFormatId: number;
    formatName: string;
    operatorId?: number;
    operatorName?: string;
  }>;
}

export default function OperatorReassignmentPage() {
  // State
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [lookupMode, setLookupMode] = useState<LookupMode>('by-operator');
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [selectedAssignments, setSelectedAssignments] = useState<Set<string>>(new Set()); // "storeId-formatId"
  const [expandedStores, setExpandedStores] = useState<Set<number>>(new Set());
  const [newOperatorId, setNewOperatorId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch retailers
  const { data: retailers = [], isLoading: retailersLoading } = useQuery<Retailer[]>({
    queryKey: ['/api/retailers'],
    queryFn: async () => {
      const res = await apiFetch('/api/retailers');
      if (!res.ok) throw new Error('Failed to fetch retailers');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
  });

  // Fetch booking cycles for selected retailer
  const { data: cycles = [], isLoading: cyclesLoading } = useQuery<BookingCycle[]>({
    queryKey: ['/api/booking-cycles', selectedRetailerId],
    queryFn: async () => {
      if (!selectedRetailerId) return [];
      const res = await apiFetch(`/api/booking-cycles?retailerId=${selectedRetailerId}`);
      if (!res.ok) throw new Error('Failed to fetch cycles');
      const data = await res.json();
      const allCycles = Array.isArray(data) ? data : (data.data || []);
      // Filter to this retailer and sort by ID descending (most recent first)
      return allCycles
        .filter((c: any) => c.retailerId === selectedRetailerId || c.retailer_id === selectedRetailerId)
        .sort((a: any, b: any) => b.id - a.id);
    },
    enabled: !!selectedRetailerId,
  });

  // Fetch operators
  const { data: operators = [], isLoading: operatorsLoading } = useQuery<Operator[]>({
    queryKey: ['/api/users/operators'],
    queryFn: async () => {
      const res = await apiFetch('/api/users/operators?pageSize=500');
      if (!res.ok) throw new Error('Failed to fetch operators');
      const data = await res.json();
      // Handle paginated or flat response
      return Array.isArray(data) ? data : (data.data || []);
    },
  });

  // Fetch assignments for selected operator (filtered by retailer) — "By Operator" mode
  const { data: operatorAssignments = [], isLoading: operatorAssignmentsLoading, refetch: refetchOperatorAssignments } = useQuery<StoreFormatAssignment[]>({
    queryKey: ['/api/store-format-operator-assignments', 'by-operator', selectedOperatorId, selectedRetailerId],
    queryFn: async () => {
      if (!selectedOperatorId) return [];
      const params = new URLSearchParams();
      params.set('operatorId', String(selectedOperatorId));
      params.set('withNames', 'true');
      if (selectedRetailerId) {
        params.set('retailerId', String(selectedRetailerId));
      }
      const res = await apiFetch(`/api/store-format-operator-assignments?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch assignments');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
    enabled: lookupMode === 'by-operator' && !!selectedOperatorId,
  });

  // Fetch assignments for selected store — "By Store" mode
  const { data: storeAssignments = [], isLoading: storeAssignmentsLoading, refetch: refetchStoreAssignments } = useQuery<StoreFormatAssignment[]>({
    queryKey: ['/api/store-format-operator-assignments', 'by-store', selectedStoreId],
    queryFn: async () => {
      if (!selectedStoreId) return [];
      const res = await apiFetch(`/api/store-format-operator-assignments?storeIds=${selectedStoreId}`);
      if (!res.ok) throw new Error('Failed to fetch assignments');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
    enabled: lookupMode === 'by-store' && !!selectedStoreId,
  });

  // Unified assignments and loading state based on mode
  const assignments = lookupMode === 'by-operator' ? operatorAssignments : storeAssignments;
  const assignmentsLoading = lookupMode === 'by-operator' ? operatorAssignmentsLoading : storeAssignmentsLoading;
  const refetchAssignments = lookupMode === 'by-operator' ? refetchOperatorAssignments : refetchStoreAssignments;

  // Fetch stores for names and retailer filtering
  const { data: stores = [] } = useQuery<Array<{ id: number; name: string; retailerId?: number }>>({
    queryKey: ['/api/stores', 'names'],
    queryFn: async () => {
      const res = await apiFetch('/api/stores?pageSize=1000');
      if (!res.ok) throw new Error('Failed to fetch stores');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
  });

  // Fetch formats for names (if not included in assignments)
  const { data: formats = [] } = useQuery<Array<{ id: number; name: string }>>({
    queryKey: ['/api/media-formats', 'names'],
    queryFn: async () => {
      const res = await apiFetch('/api/media-formats?pageSize=500');
      if (!res.ok) throw new Error('Failed to fetch formats');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
  });

  // Stores filtered by selected retailer (for "By Store" combobox)
  const retailerStores = useMemo(() => {
    if (!selectedRetailerId) return [];
    return stores
      .filter(s => s.retailerId === selectedRetailerId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [stores, selectedRetailerId]);

  // Build store-centric grouped view (filtered by retailer)
  const storeGroups: StoreGroup[] = useMemo(() => {
    const storeMap = new Map<number, StoreGroup>();
    
    const retailerStoreIds = selectedRetailerId
      ? new Set(stores.filter(s => s.retailerId === selectedRetailerId).map(s => s.id))
      : null;
    
    for (const assignment of assignments) {
      const storeId = assignment.storeId;
      
      if (retailerStoreIds && !retailerStoreIds.has(storeId)) {
        continue;
      }
      
      const storeName = assignment.storeName || stores.find(s => s.id === storeId)?.name || `Store ${storeId}`;
      const formatName = assignment.formatName || formats.find(f => f.id === assignment.mediaFormatId)?.name || `Format ${assignment.mediaFormatId}`;
      const operator = operators.find(o => o.id === assignment.operatorId);
      
      if (!storeMap.has(storeId)) {
        storeMap.set(storeId, {
          storeId,
          storeName,
          formats: [],
        });
      }
      
      storeMap.get(storeId)!.formats.push({
        assignmentId: assignment.id,
        mediaFormatId: assignment.mediaFormatId,
        formatName,
        operatorId: assignment.operatorId,
        operatorName: operator?.name,
      });
    }
    
    return Array.from(storeMap.values()).sort((a, b) => a.storeName.localeCompare(b.storeName));
  }, [assignments, stores, formats, operators, selectedRetailerId]);

  // Selection helpers
  const toggleStore = (storeId: number) => {
    setExpandedStores(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) {
        next.delete(storeId);
      } else {
        next.add(storeId);
      }
      return next;
    });
  };

  const toggleAssignment = (storeId: number, formatId: number) => {
    const key = `${storeId}-${formatId}`;
    setSelectedAssignments(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllFormatsForStore = (storeId: number, formats: StoreGroup['formats']) => {
    const keys = formats.map(f => `${storeId}-${f.mediaFormatId}`);
    const allSelected = keys.every(k => selectedAssignments.has(k));
    
    setSelectedAssignments(prev => {
      const next = new Set(prev);
      if (allSelected) {
        keys.forEach(k => next.delete(k));
      } else {
        keys.forEach(k => next.add(k));
      }
      return next;
    });
  };

  const toggleAllStores = () => {
    const allKeys = storeGroups.flatMap(g => g.formats.map(f => `${g.storeId}-${f.mediaFormatId}`));
    const allSelected = allKeys.length > 0 && allKeys.every(k => selectedAssignments.has(k));
    
    setSelectedAssignments(allSelected ? new Set() : new Set(allKeys));
  };

  // Selection summary
  const selectedCount = selectedAssignments.size;
  const selectedStoreIds = new Set(
    Array.from(selectedAssignments).map(k => parseInt(k.split('-')[0]))
  );
  const selectedStoreCount = selectedStoreIds.size;

  // Build changes for modal — derives currentOperatorId per assignment
  // so it works for both "by operator" (single source) and "by store" (mixed operators)
  const buildChanges = (): AssignmentChange[] => {
    if (!newOperatorId) return [];
    
    const newOperator = operators.find(o => o.id === newOperatorId);
    
    return Array.from(selectedAssignments).map(key => {
      const [storeIdStr, formatIdStr] = key.split('-');
      const storeId = parseInt(storeIdStr);
      const formatId = parseInt(formatIdStr);
      
      const assignment = assignments.find(a => a.storeId === storeId && a.mediaFormatId === formatId);
      const currentOpId = assignment?.operatorId ?? selectedOperatorId!;
      const currentOperator = operators.find(o => o.id === currentOpId);
      const store = stores.find(s => s.id === storeId);
      const format = formats.find(f => f.id === formatId);
      
      return {
        storeId,
        mediaFormatId: formatId,
        newOperatorId,
        currentOperatorId: currentOpId,
        storeName: store?.name,
        formatName: format?.name,
        currentOperatorName: currentOperator?.name,
        newOperatorName: newOperator?.name,
      };
    });
  };

  // Get selected retailer and cycle names for display
  const selectedRetailer = retailers.find(r => r.id === selectedRetailerId);
  const selectedCycle = cycles.find(c => c.id === selectedCycleId);

  const handlePreviewClick = () => {
    if (selectedCount > 0 && newOperatorId) {
      setShowModal(true);
    }
  };

  const handleReassignmentComplete = () => {
    setSelectedAssignments(new Set());
    setNewOperatorId(null);
    refetchAssignments();
  };

  const currentOperator = operators.find(o => o.id === selectedOperatorId);
  const hasSelection = lookupMode === 'by-operator' ? !!selectedOperatorId : !!selectedStoreId;

  // In store mode, newOperatorId must not match ALL selected assignments' operators
  // (it can match some — the backend handles per-assignment validation)
  const canPreview = selectedCount > 0 && newOperatorId && selectedRetailerId && selectedCycleId && hasSelection &&
    (lookupMode === 'by-store' || newOperatorId !== selectedOperatorId);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={ArrowRightLeft}
        title="Operator Reassignment"
        description="Reassign store × format posting responsibilities — search by operator or by store"
      />

      {/* Help/Explanation Box */}
      <Alert className="bg-blue-50 border-blue-200">
        <HelpCircle className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">How Operator Reassignment Works</AlertTitle>
        <AlertDescription className="text-blue-700 mt-2 space-y-2">
          <p>
            Reassign store × format posting responsibilities between operators.
            After selecting a retailer and cycle, choose how to find assignments:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>By Operator</strong> — select an operator to see all their stores and formats, then pick which to move</li>
            <li><strong>By Store</strong> — search for a specific store to see all formats and their current operators, then pick which to reassign</li>
          </ul>
          <p className="font-medium mt-2">The process:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Select <strong>retailer</strong> and <strong>cycle</strong></li>
            <li>Choose <strong>By Operator</strong> or <strong>By Store</strong> and make your selection</li>
            <li>Tick the <strong>store/format combinations</strong> to reassign</li>
            <li>Select the <strong>new operator</strong></li>
            <li><strong>Preview</strong> the impact, then <strong>approve</strong> to execute</li>
          </ol>
          <p className="text-sm italic mt-2">
            <strong>Note:</strong> Active mobile tasks for the outgoing operator will be superseded and new tasks generated for the incoming operator.
            This operation cannot be undone — review the impact preview carefully before approving.
          </p>
        </AlertDescription>
      </Alert>

      {/* Step 1: Select Retailer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Store className="h-5 w-5" />
            Step 1: Select Retailer
          </CardTitle>
          <CardDescription>
            Choose the retailer whose stores you want to reassign
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedRetailerId?.toString() || ''}
            onValueChange={(val) => {
              setSelectedRetailerId(val ? parseInt(val) : null);
              setSelectedCycleId(null);
              setSelectedOperatorId(null);
              setSelectedStoreId(null);
              setSelectedAssignments(new Set());
              setExpandedStores(new Set());
              setNewOperatorId(null);
            }}
          >
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a retailer..." />
            </SelectTrigger>
            <SelectContent>
              {retailersLoading ? (
                <SelectItem value="loading" disabled>Loading retailers...</SelectItem>
              ) : (
                retailers.map(retailer => (
                  <SelectItem key={retailer.id} value={retailer.id.toString()}>
                    {retailer.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Step 2: Select Cycle */}
      {selectedRetailerId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5" />
              Step 2: Select Cycle
            </CardTitle>
            <CardDescription>
              Choose the cycle from which this reassignment will be effective
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedCycleId?.toString() || ''}
              onValueChange={(val) => {
                setSelectedCycleId(val ? parseInt(val) : null);
                setSelectedOperatorId(null);
                setSelectedStoreId(null);
                setSelectedAssignments(new Set());
                setExpandedStores(new Set());
                setNewOperatorId(null);
              }}
            >
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select a cycle..." />
              </SelectTrigger>
              <SelectContent>
                {cyclesLoading ? (
                  <SelectItem value="loading" disabled>Loading cycles...</SelectItem>
                ) : cycles.length === 0 ? (
                  <SelectItem value="none" disabled>No cycles found for this retailer</SelectItem>
                ) : (
                  cycles.map(cycle => (
                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Lookup Mode + Operator/Store selector */}
      {selectedRetailerId && selectedCycleId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {lookupMode === 'by-operator' ? (
                <Users className="h-5 w-5" />
              ) : (
                <Store className="h-5 w-5" />
              )}
              Step 3: {lookupMode === 'by-operator' ? 'Select Operator' : 'Select Store'}
            </CardTitle>
            <CardDescription>
              Choose how to find assignments to reassign for {selectedRetailer?.name}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mode toggle */}
            <div className="flex gap-2">
              <Button
                variant={lookupMode === 'by-operator' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setLookupMode('by-operator');
                  setSelectedStoreId(null);
                  setSelectedAssignments(new Set());
                  setExpandedStores(new Set());
                  setNewOperatorId(null);
                }}
              >
                <Users className="h-4 w-4 mr-2" />
                By Operator
              </Button>
              <Button
                variant={lookupMode === 'by-store' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setLookupMode('by-store');
                  setSelectedOperatorId(null);
                  setSelectedAssignments(new Set());
                  setExpandedStores(new Set());
                  setNewOperatorId(null);
                }}
              >
                <Store className="h-4 w-4 mr-2" />
                By Store
              </Button>
            </div>

            {/* Operator selector */}
            {lookupMode === 'by-operator' && (
              <Select
                value={selectedOperatorId?.toString() || ''}
                onValueChange={(val) => {
                  setSelectedOperatorId(val ? parseInt(val) : null);
                  setSelectedAssignments(new Set());
                  setExpandedStores(new Set());
                  setNewOperatorId(null);
                }}
              >
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Select an operator..." />
                </SelectTrigger>
                <SelectContent>
                  {operatorsLoading ? (
                    <SelectItem value="loading" disabled>Loading operators...</SelectItem>
                  ) : (
                    operators.map(op => (
                      <SelectItem key={op.id} value={op.id.toString()}>
                        {op.name} {op.email ? `(${op.email})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}

            {/* Store selector (searchable combobox) */}
            {lookupMode === 'by-store' && (
              <div className="max-w-md">
                <Combobox
                  options={retailerStores.map(s => ({
                    value: s.id.toString(),
                    label: s.name,
                  }))}
                  value={selectedStoreId?.toString() || ''}
                  onValueChange={(val) => {
                    setSelectedStoreId(val ? parseInt(val) : null);
                    setSelectedAssignments(new Set());
                    setExpandedStores(new Set());
                    setNewOperatorId(null);
                  }}
                  placeholder="Search for a store..."
                  searchPlaceholder="Type to search stores..."
                  emptyText="No stores found for this retailer"
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Select Assignments */}
      {selectedRetailerId && selectedCycleId && hasSelection && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Store className="h-5 w-5" />
              Step 4: Select Assignments
            </CardTitle>
            <CardDescription>
              {lookupMode === 'by-operator'
                ? `${currentOperator?.name}'s current store × format assignments for ${selectedRetailer?.name}`
                : `Format assignments at ${stores.find(s => s.id === selectedStoreId)?.name || 'selected store'}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {assignmentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading assignments...</span>
              </div>
            ) : storeGroups.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No assignments found for this operator
              </div>
            ) : (
              <>
                {/* Select All Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedCount > 0 && selectedCount === assignments.length}
                      onCheckedChange={() => toggleAllStores()}
                    />
                    <span className="font-medium">Select All</span>
                  </div>
                  <Badge variant="secondary">
                    {storeGroups.length} store{storeGroups.length !== 1 ? 's' : ''}, {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
                  </Badge>
                </div>

                {/* Store Groups */}
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {storeGroups.map(group => {
                      const isExpanded = expandedStores.has(group.storeId);
                      const storeFormatKeys = group.formats.map(f => `${group.storeId}-${f.mediaFormatId}`);
                      const selectedInStore = storeFormatKeys.filter(k => selectedAssignments.has(k)).length;
                      const allSelected = selectedInStore === group.formats.length;
                      const someSelected = selectedInStore > 0 && !allSelected;

                      return (
                        <div key={group.storeId} className="border rounded-lg">
                          {/* Store Header */}
                          <div 
                            className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                            onClick={() => toggleStore(group.storeId)}
                          >
                            <button 
                              className="p-0.5"
                              onClick={(e) => { e.stopPropagation(); toggleStore(group.storeId); }}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <Checkbox
                              checked={allSelected}
                              className={someSelected ? 'data-[state=checked]:bg-gray-400' : ''}
                              onCheckedChange={() => toggleAllFormatsForStore(group.storeId, group.formats)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Store className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium flex-1">{group.storeName}</span>
                            <Badge variant="outline" className="text-xs">
                              {selectedInStore}/{group.formats.length} selected
                            </Badge>
                          </div>

                          {/* Formats (Expanded) */}
                          {isExpanded && (
                            <div className="border-t bg-muted/30 p-3 pl-12 space-y-2">
                              {group.formats.map(format => {
                                const key = `${group.storeId}-${format.mediaFormatId}`;
                                const isSelected = selectedAssignments.has(key);

                                return (
                                  <div 
                                    key={format.mediaFormatId}
                                    className="flex items-center gap-3"
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleAssignment(group.storeId, format.mediaFormatId)}
                                    />
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm">{format.formatName}</span>
                                    {lookupMode === 'by-store' && format.operatorName && (
                                      <Badge variant="outline" className="text-xs ml-auto">
                                        <Users className="h-3 w-3 mr-1" />
                                        {format.operatorName}
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Choose New Operator */}
      {selectedRetailerId && selectedCycleId && hasSelection && selectedCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ArrowRight className="h-5 w-5" />
              Step 5: Choose New Operator
            </CardTitle>
            <CardDescription>
              Select the operator to receive these assignments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={newOperatorId?.toString() || ''}
              onValueChange={(val) => setNewOperatorId(val ? parseInt(val) : null)}
            >
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Select new operator..." />
              </SelectTrigger>
              <SelectContent>
                {operators
                  .filter(op => lookupMode === 'by-operator'
                    ? op.id !== selectedOperatorId
                    : true
                  )
                  .map(op => (
                    <SelectItem key={op.id} value={op.id.toString()}>
                      {op.name} {op.email ? `(${op.email})` : ''}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Selection Summary Bar (Sticky Footer) */}
      {selectedRetailerId && selectedCycleId && hasSelection && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-sm">
                <span className="text-muted-foreground">{selectedRetailer?.name}</span>
                <span className="text-muted-foreground"> • </span>
                <span className="text-muted-foreground">{selectedCycle?.name}</span>
              </div>
              <Separator orientation="vertical" className="h-6" />
              <div className="text-sm">
                <span className="font-medium">{selectedCount}</span>
                <span className="text-muted-foreground"> format{selectedCount !== 1 ? 's' : ''} selected</span>
              </div>
              <Separator orientation="vertical" className="h-6" />
              <div className="text-sm">
                <span className="font-medium">{selectedStoreCount}</span>
                <span className="text-muted-foreground"> store{selectedStoreCount !== 1 ? 's' : ''}</span>
              </div>
              {lookupMode === 'by-operator' && (
                <>
                  <Separator orientation="vertical" className="h-6" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">From: </span>
                    <span className="font-medium">{currentOperator?.name || 'None'}</span>
                  </div>
                </>
              )}
              {newOperatorId && (
                <>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">To: </span>
                    <span className="font-medium text-green-600">
                      {operators.find(o => o.id === newOperatorId)?.name || 'None'}
                    </span>
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={handlePreviewClick}
              disabled={!canPreview}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Preview Reassignment Impact
            </Button>
          </div>
        </div>
      )}

      {/* Bottom padding for sticky footer */}
      {selectedRetailerId && selectedCycleId && hasSelection && <div className="h-20" />}

      {/* Reassignment Impact Modal */}
      <ReassignmentImpactModal
        open={showModal}
        onOpenChange={setShowModal}
        changes={buildChanges()}
        cycleId={selectedCycleId ?? undefined}
        retailerId={selectedRetailerId ?? undefined}
        onComplete={handleReassignmentComplete}
      />
    </div>
  );
}
